import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface RootErrorBoundaryProps {
  error?: Error | unknown;
}

export const RootErrorBoundary = ({ error }: RootErrorBoundaryProps = {}) => {

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle>Something went wrong</CardTitle>
          </div>
          <CardDescription>
            We've been notified and are working on a fix.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error instanceof Error && import.meta.env.DEV && (
            <div className="rounded-lg bg-destructive/10 p-3">
              <p className="text-sm font-mono text-destructive">{error.message}</p>
              {error.stack && (
                <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-40">
                  {error.stack}
                </pre>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={() => window.location.href = "/"} variant="outline">
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
